import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo } from 'react';
import { BoxHelper, BufferAttribute, BufferGeometry, type Mesh } from 'three';
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js';
import { useShallow } from 'zustand/shallow';

import { convertToFloatArray } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { createPois } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { BuildingModel } from 'api/models';

interface BuildingMeshProps {
    buildingModel: BuildingModel;
}

export const BuildingMesh = ({ buildingModel }: BuildingMeshProps) => {
    const {
        shadowsEnabled,
        buildingHelperRef,
        mainCameraControlsRef,
        setPois,
        setColliders,
        fitToObject,
        setBuildingHelperRef,
        setBuildingWallEdges
    } = usePlacementStore(
        useShallow((state) => ({
            buildingHelperRef: state.buildingHelperRef,
            mainCameraControlsRef: state.mainCameraControlsRef,
            setPois: state.setPois,
            setColliders: state.setColliders,
            fitToObject: state.fitToObject,
            setBuildingHelperRef: state.setBuildingHelperRef,
            setBuildingWallEdges: state.setBuildingWallEdges,
            shadowsEnabled: state.graphicsSettings.shadowsEnabled
        }))
    );

    const gl = useThree((state) => state.gl);

    const mergedGeometry = useMemo(() => {
        const faceMeshes = buildingModel.buildingFaceMesh;
        if (faceMeshes && faceMeshes.length > 0) {
            const geometries = faceMeshes.map((faceMesh) => {
                const geometry = new BufferGeometry();
                geometry.setAttribute('position', new BufferAttribute(convertToFloatArray(faceMesh.vertices), 3));
                geometry.setIndex(faceMesh.indices);
                geometry.computeVertexNormals();
                return geometry;
            });

            return BufferGeometryUtils.mergeGeometries(geometries, false);
        }

        return new BufferGeometry();
    }, [buildingModel.buildingFaceMesh]);

    useEffect(() => {
        return () => {
            mergedGeometry.dispose();
            setBuildingHelperRef(null);
        };
    }, [mergedGeometry, setBuildingHelperRef]);

    useEffect(() => {
        const { pois, colliderPoints, wallEdges } = createPois(buildingModel.pointsOfInterest);
        setPois(pois);
        setColliders(colliderPoints, 'building');
        setBuildingWallEdges(wallEdges);
    }, [buildingModel.pointsOfInterest, setBuildingWallEdges, setColliders, setPois]);

    const handleBuildingMeshRef = useCallback(
        (buildingMesh: Mesh | null) => {
            if (buildingMesh) {
                if (!buildingHelperRef.current) {
                    setBuildingHelperRef(new BoxHelper(buildingMesh, 0xffff00));
                }

                if (buildingHelperRef.current && gl.domElement && mainCameraControlsRef.current) {
                    fitToObject(buildingHelperRef.current, mainCameraControlsRef, true, true, gl.domElement);
                    mainCameraControlsRef.current.enabled = true;
                }
            }
        },
        [buildingHelperRef, mainCameraControlsRef, fitToObject, gl.domElement, setBuildingHelperRef]
    );

    if (!mergedGeometry) {
        return null;
    }

    return (
        <group>
            <mesh ref={handleBuildingMeshRef} receiveShadow={shadowsEnabled}>
                <primitive attach="geometry" object={mergedGeometry} />
                <meshBasicMaterial color={'#e9ebf2'} />
            </mesh>
            <lineSegments>
                <edgesGeometry args={[mergedGeometry, 45]} />
                <lineBasicMaterial color={'gray'} />
            </lineSegments>
        </group>
    );
};
